// How wide each column of the Me shell is, from the window's width.
//
// Promise: the detail keeps the width a document needs (DOC_WIDTH) as long as it
// can. What gives way first is the list (down to LIST_MIN), then the sidebar
// (down to SIDEBAR_MIN), and then the sidebar leaves the row and becomes a
// drawer (compact). A frame whose screens have no list beside a document
// (settings) keeps no room for one (`list: false`), so its
// sidebar stays in the row longer. The rail, when the host has one, is always
// there: in the row in the wide layout, in the drawer in the compact one.
// A folded sidebar (`collapsed`) needs only SIDEBAR_COLLAPSED, so the row
// lasts longer with it. A frame that can fold (`foldable`) folds by itself
// where the full sidebar no longer fits (`byWidth`), and becomes a drawer only
// at a phone's width (PHONE_MAX and below).
import { useSyncExternalStore } from "react";
import {
  LIST_MIN,
  SIDEBAR_COLLAPSED,
  SIDEBAR_MAX,
  SIDEBAR_MIN,
} from "../device/settings";

export const RAIL_WIDTH = 52;
/** A document's column (760) + its outline rail (32) + a thin scrollbar (10). */
export const DOC_WIDTH = 760 + 32 + 10;
/**
 * A settings page's column (640) with the panel's padding (2 × 24) and a thin
 * scrollbar (10): a settings page needs no more room than this.
 */
export const SETTINGS_WIDTH = 640 + 48 + 10;
/** Space outside the panel: the right margin (8) and the panel's borders. */
export const PANEL_GUTTER = 10;
/** A phone's width: the sidebar is a drawer here even when it could fold. */
export const PHONE_MAX = 600;

export interface LeftLayout {
  compact: boolean;
  /** The sidebar is in the row folded to its icons. */
  collapsed: boolean;
  /** Folded because the window is too narrow for it (unfolding opens it over the panel instead). */
  byWidth: boolean;
  sidebarWidth: number;
  /** How wide dragging may make the sidebar without leaving the row. */
  sidebarMax: number;
}

export function leftLayout(
  viewportWidth: number,
  opts: {
    rail: boolean;
    sidebarPref: number;
    list?: boolean;
    /** The frame has a folded sidebar. */
    foldable?: boolean;
    /** Folded by choice (kept on the device). */
    collapsed?: boolean;
    /** The room its page needs beside the sidebar (default: a document's). */
    content?: number;
  },
): LeftLayout {
  const room =
    viewportWidth -
    (opts.rail ? RAIL_WIDTH : 0) -
    PANEL_GUTTER -
    (opts.list === false ? 0 : LIST_MIN) -
    (opts.content ?? DOC_WIDTH);
  const sidebarMax = Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, room));
  const narrow = room < SIDEBAR_MIN;
  if (
    opts.foldable &&
    viewportWidth > PHONE_MAX &&
    (narrow || (opts.collapsed && room >= SIDEBAR_COLLAPSED))
  )
    return {
      compact: false,
      collapsed: true,
      byWidth: narrow,
      sidebarWidth: SIDEBAR_COLLAPSED,
      sidebarMax,
    };
  if (narrow)
    return {
      compact: true,
      collapsed: false,
      byWidth: false,
      sidebarWidth: opts.sidebarPref,
      sidebarMax,
    };
  return {
    compact: false,
    collapsed: false,
    byWidth: false,
    sidebarWidth: Math.min(opts.sidebarPref, room),
    sidebarMax,
  };
}

/** The panel's width from the window's and the columns beside it. */
export function panelWidthOf(
  viewportWidth: number,
  layout: LeftLayout,
  rail: boolean,
): number {
  if (layout.compact) return viewportWidth;
  return (
    viewportWidth - (rail ? RAIL_WIDTH : 0) - layout.sidebarWidth - PANEL_GUTTER
  );
}

const subscribe = (fn: () => void) => {
  window.addEventListener("resize", fn);
  return () => window.removeEventListener("resize", fn);
};
const width = () => window.innerWidth;

export function useViewportWidth(): number {
  return useSyncExternalStore(subscribe, width, () => 1440);
}

/**
 * What the system keeps at the screen's right side (`safe-area-inset-right`):
 * the iPhone Duo's column for its clock and camera (84 pt, on both its
 * screens), a landscape iPhone's notch. Read from a box sized by it.
 */
let insetProbe: HTMLElement | null = null;
const rightInset = () => {
  if (typeof document === "undefined") return 0;
  if (!insetProbe) {
    insetProbe = document.createElement("div");
    insetProbe.setAttribute("aria-hidden", "true");
    insetProbe.style.cssText =
      "position:fixed;top:0;left:0;height:0;visibility:hidden;pointer-events:none;width:env(safe-area-inset-right,0px)";
    document.body.appendChild(insetProbe);
  }
  return insetProbe.offsetWidth;
};

export function useRightInset(): number {
  return useSyncExternalStore(subscribe, rightInset, () => 0);
}
